import React, { useState, useEffect } from 'react';
import { usersAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Loader2,
  Shield,
  User,
  KeyRound,
  Pencil,
  Archive,
  ArchiveRestore,
} from 'lucide-react';

const UsersPage = () => {
  const { user, isAdmin, isManager } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'vendeur',
    manager_id: '',
  });

  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: 'vendeur',
    manager_id: '',
  });

  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [showArchived]);

  const managers = users.filter((user) => user.role === 'manager' && !user.archived);
  const teamCounts = users.reduce((counts, user) => {
    if (user.role === 'vendeur' && user.manager_id) {
      counts[user.manager_id] = (counts[user.manager_id] || 0) + 1;
    }
    return counts;
  }, {});

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      // Filter based on showArchived
      let filteredUsers = response.data;
      if (!showArchived) {
        filteredUsers = response.data.filter(u => !u.archived);
      }
      setUsers(filteredUsers);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const canManageUser = (targetUser) => {
    if (isAdmin()) return true;
    if (!isManager()) return false;
    return targetUser.role === 'vendeur' && targetUser.manager_id === user?.id;
  };

  const handleArchive = async (user) => {
    if (!window.confirm(`Archiver l'utilisateur ${user.name} ?`)) return;
    try {
      await usersAPI.archive(user.id);
      toast.success('Utilisateur archivé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'archivage');
    }
  };

  const handleUnarchive = async (user) => {
    try {
      await usersAPI.unarchive(user.id);
      toast.success('Utilisateur désarchivé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await usersAPI.create({
        ...form,
        role: isManager() ? 'vendeur' : form.role,
        manager_id: isManager() ? user?.id : (form.role === 'vendeur' ? (form.manager_id || null) : null),
      });
      toast.success('Utilisateur créé');
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    
    try {
      await usersAPI.delete(userId);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setFormLoading(true);
    try {
      await usersAPI.resetPassword(selectedUser.id, newPassword);
      toast.success(`Mot de passe de ${selectedUser.name} réinitialisé`);
      setResetDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setFormLoading(false);
    }
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role || 'vendeur',
      manager_id: user.manager_id || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditUser = async () => {
    if (!selectedUser || !editForm.name || !editForm.email) {
      toast.error('Nom et email requis');
      return;
    }
    
    setFormLoading(true);
    try {
      await usersAPI.update(selectedUser.id, {
        name: editForm.name,
        email: editForm.email,
        role: isManager() ? 'vendeur' : editForm.role,
        manager_id: isManager() ? user?.id : (editForm.role === 'vendeur' ? (editForm.manager_id || null) : null),
      });
      toast.success('Utilisateur modifié');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      password: '',
      role: 'vendeur',
      manager_id: '',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Utilisateurs
          </h1>
          <p className="text-slate-500 mt-1">{users.length} utilisateur{users.length > 1 ? 's' : ''}</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={showArchived ? "secondary" : "outline"}
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
          >
            <Archive className="w-4 h-4 mr-2" />
            {showArchived ? 'Masquer archivés' : 'Voir archivés'}
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="new-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvel utilisateur
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                Nouvel utilisateur
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom complet *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe *</Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="user-password-input"
                />
              </div>
              {isAdmin() ? (
                <div className="space-y-2">
                  <Label htmlFor="role">Rôle *</Label>
                  <Select
                    value={form.role}
                    onValueChange={(value) => setForm({
                      ...form,
                      role: value,
                      manager_id: value === 'vendeur' ? form.manager_id : '',
                    })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vendeur">Vendeur</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                  Le compte créé sera automatiquement ajouté comme vendeur de votre équipe.
                </div>
              )}
              {(isAdmin() && form.role === 'vendeur') && (
                <div className="space-y-2">
                  <Label htmlFor="manager">Manager d'équipe</Label>
                  <Select
                    value={form.manager_id || 'none'}
                    onValueChange={(value) => setForm({ ...form, manager_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger id="manager">
                      <SelectValue placeholder="Sélectionner un manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun manager</SelectItem>
                      {managers.map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          {manager.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Le manager pourra récupérer les clients de son équipe puis les redistribuer à ses commerciaux.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="bg-slate-800 hover:bg-slate-900"
                  disabled={formLoading}
                  data-testid="save-user-btn"
                >
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Créer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {users.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Aucun utilisateur
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Utilisateur
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Email
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Rôle
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Équipe
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Créé le
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`} className={user.archived ? 'opacity-50 bg-slate-50' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full ${user.archived ? 'bg-slate-400' : 'bg-slate-800'} text-white flex items-center justify-center text-sm font-medium`}>
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className="font-medium text-slate-800">{user.name}</span>
                            {user.archived && (
                              <Badge variant="outline" className="ml-2 text-xs border-slate-300 text-slate-500">
                                Archivé
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          user.role === 'admin'
                            ? 'bg-yellow-100 text-yellow-700'
                            : user.role === 'manager'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-slate-100 text-slate-600'
                        }>
                          <Shield className="w-3 h-3 mr-1" />
                          {user.role === 'admin' ? 'Admin' : user.role === 'manager' ? 'Manager' : 'Vendeur'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {user.role === 'manager' && (
                          <span>{teamCounts[user.id] || 0} vendeur{(teamCounts[user.id] || 0) > 1 ? 's' : ''}</span>
                        )}
                        {user.role === 'vendeur' && (
                          <span>{user.manager_name || 'Aucun manager'}</span>
                        )}
                        {user.role === 'admin' && <span>-</span>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!user.archived ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(user)}
                                disabled={!canManageUser(user)}
                                className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
                                title="Modifier"
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openResetDialog(user)}
                                disabled={!canManageUser(user)}
                                className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                                title="Réinitialiser le mot de passe"
                                data-testid={`reset-password-${user.id}`}
                              >
                                <KeyRound className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleArchive(user)}
                                disabled={!canManageUser(user)}
                                className="text-slate-500 hover:text-slate-600"
                                title="Archiver"
                              >
                                <Archive className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleUnarchive(user)}
                                disabled={!canManageUser(user)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Désarchiver"
                              >
                                <ArchiveRestore className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(user.id)}
                                disabled={!canManageUser(user)}
                                className="text-red-500 hover:text-red-600"
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={(open) => {
        setResetDialogOpen(open);
        if (!open) {
          setSelectedUser(null);
          setNewPassword('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              Réinitialiser le mot de passe
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Définir un nouveau mot de passe pour <strong>{selectedUser.name}</strong> ({selectedUser.email})
              </p>
              <div className="space-y-2">
                <Label htmlFor="new-password">Nouveau mot de passe *</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 caractères"
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setResetDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  onClick={handleResetPassword}
                  className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                  disabled={formLoading || newPassword.length < 6}
                  data-testid="confirm-reset-password-btn"
                >
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Réinitialiser
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) {
          setSelectedUser(null);
          setEditForm({ name: '', email: '', role: 'vendeur', manager_id: '' });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              Modifier l'utilisateur
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nom complet *</Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Nom complet"
                  data-testid="edit-user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="email@exemple.com"
                  data-testid="edit-user-email-input"
                />
              </div>
              {isAdmin() ? (
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Rôle</Label>
                  <Select
                    value={editForm.role}
                    onValueChange={(value) => setEditForm({
                      ...editForm,
                      role: value,
                      manager_id: value === 'vendeur' ? editForm.manager_id : '',
                    })}
                  >
                    <SelectTrigger id="edit-role" data-testid="edit-user-role-select">
                      <SelectValue placeholder="Sélectionner un rôle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vendeur">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-500" />
                          <div>
                            <div className="font-medium">Vendeur</div>
                            <div className="text-xs text-slate-500">Gestion de ses propres devis et clients</div>
                          </div>
                        </div>
                      </SelectItem>
                      <SelectItem value="manager">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-blue-500" />
                          <div>
                            <div className="font-medium">Manager</div>
                            <div className="text-xs text-slate-500">Accès équipe + rapport activité équipe</div>
                          </div>
                        </div>
                      </SelectItem>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-yellow-500" />
                          <div>
                            <div className="font-medium">Administrateur</div>
                            <div className="text-xs text-slate-500">Accès complet à toutes les fonctions</div>
                          </div>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {editForm.role === 'manager' && (
                    <p className="text-xs text-blue-600 bg-blue-50 rounded px-3 py-2">
                      Le manager recevra son rapport individuel + le rapport consolidé de l'équipe chaque jour.
                    </p>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                  Un manager peut modifier les vendeurs de son équipe, sans changer leur rôle.
                </div>
              )}
              {(isAdmin() && editForm.role === 'vendeur') && (
                <div className="space-y-2">
                  <Label htmlFor="edit-manager">Manager d'équipe</Label>
                  <Select
                    value={editForm.manager_id || 'none'}
                    onValueChange={(value) => setEditForm({ ...editForm, manager_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger id="edit-manager">
                      <SelectValue placeholder="Sélectionner un manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun manager</SelectItem>
                      {managers
                        .filter((manager) => manager.id !== selectedUser?.id)
                        .map((manager) => (
                          <SelectItem key={manager.id} value={manager.id}>
                            {manager.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  onClick={handleEditUser}
                  className="bg-orange-500 hover:bg-orange-600"
                  disabled={formLoading || !editForm.name || !editForm.email}
                  data-testid="confirm-edit-user-btn"
                >
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
